import { topUpAmountProblem } from "@/modules/dashboard/wallet-top-ups/utils/walletTopUp";
import { PROVIDER_DISPLAY_NAMES } from "@/modules/dashboard/wallets/constants/wallets.constants";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { ALLOWED_ATTACHMENT_TYPES, DEFAULT_MAX_SIZE_MB } from "@/shared/utils/fileValidation";

import { INCIDENT_STALE_AFTER_DAYS } from "../constants/paymentIncidents.constants";
import type {
  PaymentIncident,
  PaymentIncidentStatus,
  PaymentIncidentType,
  RecordableIncidentAction,
} from "../types/paymentIncidents.types";

export type IncidentPillVariant = "success" | "warning" | "info" | "danger" | "neutral";

/** Amber while it waits on us, blue while it waits on the provider, green once the money is back. */
export const INCIDENT_STATUS_PILL: Record<PaymentIncidentStatus, IncidentPillVariant> = {
  pending_report: "warning",
  reported: "info",
  in_progress: "info",
  provider_refunded: "success",
  resolved: "neutral",
  dismissed: "neutral",
};

const OPEN: ReadonlySet<PaymentIncidentStatus> = new Set(["pending_report", "reported", "in_progress"]);

export function isIncidentOpen(status: PaymentIncidentStatus): boolean {
  return OPEN.has(status);
}

/** Open, and nothing new for a week: time to write to the provider again. */
export function isIncidentStale(incident: Pick<PaymentIncident, "status" | "days_since_update">): boolean {
  return isIncidentOpen(incident.status) && incident.days_since_update >= INCIDENT_STALE_AFTER_DAYS;
}

/**
 * The timeline entries the incident takes in its status, in the order the panel offers them. Mirrors
 * coloca-nest `INCIDENT_TRANSITIONS`: open incidents can be answered, refunded, resolved or dismissed; closed
 * ones only take notes and can be reopened. A note is always possible.
 */
export function recordableActionsFor(status: PaymentIncidentStatus): RecordableIncidentAction[] {
  if (isIncidentOpen(status)) {
    return ["provider_responded", "provider_refunded", "resolved", "dismissed", "note"];
  }
  return ["note", "reopened"];
}

/** The four steps of a claim, and where the incident is on them; dismissed is off the track. */
export const INCIDENT_CASE_STEPS = ["pending_report", "reported", "in_progress", "closed"] as const;
export type IncidentCaseStep = (typeof INCIDENT_CASE_STEPS)[number];

export function incidentCaseStep(status: PaymentIncidentStatus): IncidentCaseStep | null {
  switch (status) {
    case "pending_report":
    case "reported":
    case "in_progress":
      return status;
    case "provider_refunded":
    case "resolved":
      return "closed";
    default:
      return null;
  }
}

/** What a movement most likely is when staff open an incident on it. */
export function defaultIncidentType(movement: {
  operation_status: string | null;
  operation_rail: string | null;
}): PaymentIncidentType {
  if (movement.operation_status === "PROCESSING" || movement.operation_status === "CREATED") {
    return "status_not_updated";
  }
  if (movement.operation_status === "PAID" && movement.operation_rail !== "USDT") {
    return "credit_not_applied";
  }
  return "other";
}

/** Incidents are about payouts: only a payout's own leg (its reserve) opens one. */
export function canOpenIncidentOn(movement: { operation_type: string }): boolean {
  return movement.operation_type === "PAYOUT";
}

/**
 * The provider's name, for the staff incident panel only. Rule 12 keeps provider identity off every screen;
 * the staff treasury tab and this panel are the agreed exceptions, since talking to the provider is their
 * job.
 */
export function incidentProviderName(code: string | null | undefined): string | null {
  if (!code) return null;
  return PROVIDER_DISPLAY_NAMES[code] ?? code;
}

/** `occurred_at` for an entry dated `day` (`YYYY-MM-DD`): omitted for today, midday that day otherwise. */
export function incidentOccurredAt(day: string, today: string): string | undefined {
  if (!day || day === today) return undefined;
  return new Date(`${day}T12:00:00`).toISOString();
}

export type IncidentRefundAmountProblem = "invalid" | "not_positive" | "precision" | "exceeds";

/** What is wrong with a refund amount, or null: positive, at the currency's precision, at most `max`. */
export function refundAmountProblem(
  amount: string,
  max: string,
  currency: string,
): IncidentRefundAmountProblem | null {
  const problem = topUpAmountProblem(amount, currency);
  if (problem) return problem;
  return compareDecimalStrings(amount.trim(), max) > 0 ? "exceeds" : null;
}

export type IncidentAttachmentRejection = "too_many" | "too_large" | "unsupported_type";

/** Why a file cannot go: the backend takes JPEG, PNG, WebP or PDF up to 10 MB each. */
export function attachmentRejection(file: Pick<File, "type" | "size">): IncidentAttachmentRejection | null {
  if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type as (typeof ALLOWED_ATTACHMENT_TYPES)[number])) {
    return "unsupported_type";
  }
  return file.size > DEFAULT_MAX_SIZE_MB * 1024 * 1024 ? "too_large" : null;
}
